import InputFiles from "@/components/Forms/Input/input-contract.vue";
import ContractList from './ContractList.vue';
import ContractHistoryModal from './ContractHistoryModal.vue';
import {showErrorSnackBar, showSuccessSnackBar} from "@/utils/SnackBartHelper";
import cloneDeep from 'lodash/cloneDeep';
import ContractRequestStorage from "@/services/storage/ContractRequestStorage";
import ContractsService from "@/services/firebase/Contracts/ContractsService";
import {mapGetters} from "vuex";
import ContractsTemplatesService from "@/services/server/ContractsTemplatesService";


const newContract = () => {
    return {
        id_doc: null,
        createdAt: new Date().getTime(),
        updatedAt: null,
        createdByName: null,
        createdById: null,
        observations: null,
        selectedTemplateId: null,
        selectedProposalId: null,
        selectedProposalType: null,
        historic: [],
        currentAttachedContract: {
            name: null,
            path: null,
        }
    }
}

export default {
    name: "newContractModal",
    components: {
        InputFiles,
        ContractList,
        ContractHistoryModal,
    },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        close: {
            type: Function,
            default: () => {}
        },
        idContract: {
            type: String,
            default: null,
        }
    },
    data: () => ({
        fileTypes: [
            'pdf',
            'docx',
        ],
        dialogDownload: false,
        fileLink: null,
        previousFile: null,
        fileToShow: null,
        currentContract: newContract(),
        showContractList: false,
        historic: [],
        historyModalOpen: false,
        isLoading: false,
        isLoadingMessage: "Carregando Contrato...",
        initialContract: null,
        selectedTemplate: null,
        isContentLoading: true,
    }),
    computed: {
        ...mapGetters(['getCurrentUser', 'getCurrentUserRank']),
        isUpdateMode() {
            return this.currentContract.id_doc !== null;
        },
        isAdminOrSuperAdmin() {
            return this.getCurrentUserRank === 'Admin' || this.getCurrentUserRank === 'Super Admin';
        }
    },
    methods: {
        showHistory() {
            this.historyModalOpen = true;
        },
        closeHistoryModal() {
            this.historyModalOpen = false;
        },
        openContractGenerationModal() {
            this.$emit('contract-generation-started');
        },
        getProposalTypeLabel(type) {
            if (!type) return 'Tipo não definido';
            
            const types = {
                'model_performance': 'Modelo Performance',
                'investment': 'Venda do Projeto',
                'pre_fixed': 'Pré-Fixado',
                'management': 'Gestão'
            };
            
            return types[type] || type;
        },
        resetContractState() {
            this.currentContract = newContract();
            this.fileToShow = null;
            this.initialContract = null;
        },
        isTemplateAlreadyUsed(templateId, existingContracts) {
            if (!existingContracts || !existingContracts.length) return false;
            
            // Skip records that have the same ID as the current one (we're editing it)
            return existingContracts.some(contract => 
                contract.selectedTemplateId === templateId && 
                contract.id_doc !== this.currentContract.id_doc
            );
        },
        async handleContractGenerated(templateId, proposalId, proposalType, contractFile, existingContracts = []) {
            // Only check for duplicate templates in non-editing mode or when switching templates
            const isEditingWithSameTemplate = this.currentContract.id_doc && 
                                            this.currentContract.selectedTemplateId === templateId;
                                            
            // If not editing the same template, check if template is used elsewhere
            if (!isEditingWithSameTemplate && this.isTemplateAlreadyUsed(templateId, existingContracts)) {
                showErrorSnackBar("Este template já está sendo usado em outro contrato. Por favor, selecione um template diferente.");
                this.$emit('contract-generation-completed', false);
                return;
            }
            
            this.currentContract = {
                ...this.currentContract,
                selectedTemplateId: templateId,
                selectedProposalId: proposalId,
            };
            
            // Buscar informações do template
            try {
                const templateData = await ContractsTemplatesService.getTemplateById(templateId);
                this.selectedTemplate = templateData.data.data;
            } catch (error) {
                console.error('Erro ao buscar template:', error);
                this.selectedTemplate = null;
            }
            
            // Não anexar o arquivo automaticamente
            // Apenas mostrar a mensagem de sucesso
            showSuccessSnackBar("Contrato baixado pronto para revisão");
            
            this.$emit('contract-generation-completed', true);
        },
        async checkIfExistsRequestsImagesToSave(idContract, request, files = []) {
            if (files.length) {
                const allowedTypes = [
                    'application/pdf',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ];
                if (this.initialRequest?.signedContractPath && this.initialRequest?.signedContractPath.length) {
                    const isArrayOfStrings = Array.isArray(this.initialRequest?.signedContractPath);

                    const paths = isArrayOfStrings
                        ? this.initialRequest.signedContractPath
                        : [this.initialRequest.signedContractPath];

                    for (let file of paths) {
                        await ContractRequestStorage.deleteImg(file);
                    }
                    request.signedContractPath = [];
                }

                request.signedContractPath = []

                for (const file of files) {
                    const filePath = await ContractRequestStorage.uploadFile(idContract, file);
                    request.signedContractPath.push(filePath);
                }

                delete request.signedContractFile;
            } else {
                const isArrayOfStrings = Array.isArray(request.signedContractPath);

                const paths = isArrayOfStrings
                    ? request.signedContractPath
                    : [request.signedContractPath];

                for (const file of paths) {
                    if (file) {
                        await ContractRequestStorage.deleteImg(file);
                    }
                }
                request.signedContractPath = [];
                this.files = [];
            }
        },
        async saveContract() {
            // Check if user has admin privileges
            if (!this.isAdminOrSuperAdmin) {
                showErrorSnackBar('Você não tem permissão para salvar alterações no contrato.');
                return;
            }
            
            if (!this.currentContract.selectedTemplateId || !this.currentContract.selectedProposalId) {
                showErrorSnackBar('É necessário gerar um contrato antes de salvar!');
                return;
            }

            if (!this.fileToShow) {
                showErrorSnackBar('É necessário anexar um arquivo de contrato antes de salvar!');
                return;
            }

            this.currentContract.currentAttachedContractFile = this.fileToShow;
            
            // Criar um payload apenas com os dados necessários para o request
            const payload = {
                ...this.currentContract,
                // Garantir que apenas os dados do request sejam incluídos
                selectedTemplateId: this.currentContract.selectedTemplateId,
                selectedProposalId: this.currentContract.selectedProposalId,
                customAnchors: this.currentContract.customAnchors || [],
                currentAttachedContract: {
                    name: this.fileToShow.name,
                    path: null // O path será preenchido após o upload
                }
            };

            this.isLoading = true;
     
            try {
                if (this.currentContract.id_doc) {
                    this.isLoadingMessage = "Atualizando Contrato...";
                    const updatedContract = await ContractsService.updateRequestContractById(this.idContract, this.initialContract, payload)
                    
                    this.$emit('update-contract', updatedContract);
                } else {
                    this.isLoadingMessage = "Criando Contrato...";
                    payload.createdById = this.getCurrentUser.currentUser.id
                    payload.createdByName = this.getCurrentUser.currentUser.name;
                    
                    const createdContract = await ContractsService.createRequestContractById(this.idContract, payload);
                    
                    this.currentContract = {
                        ...this.currentContract,
                        id_doc: createdContract.id_doc,
                        historic: createdContract.historic || []
                    };
                    
                    this.$emit('create-contract', createdContract);
                }

                

                
                showSuccessSnackBar("Contrato Salvo com sucesso!")
            } catch (e) {
                this.isLoading = false;
                console.error("Error: ", e)
                showErrorSnackBar("Aconteceu um erro inesperado, contacte o Suporte Técnico: SC-01-NCMS-82")
            } finally {
                this.isLoading = false;
            }
        },
        async downloadFile() {
            if (this.fileToShow) {
                try {
                    this.isLoading = true;
                    await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            this.fileLink = e.target.result;
                            this.dialogDownload = true;
                            resolve();
                        };
                        reader.onerror = (e) => {
                            reject(new Error("Failed to read file"));
                        };
                        reader.readAsDataURL(this.fileToShow);
                    });
                } catch (e) {
                    console.error("error", e);
                    showErrorSnackBar("Erro ao preparar download");
                } finally {
                    this.isLoading = false;
                }
            } else {
                showErrorSnackBar("Contrato não disponível");
            }
        },
        async setContractForEditing(contract) {
            try {
                this.isLoadingMessage = "Carregando Contrato...";
                this.isLoading = true;
                
                // Create a clean deep copy of the contract
                this.currentContract = cloneDeep(contract);
                this.initialContract = cloneDeep(contract);
                
                // Get latest template information to ensure fresh data
                if (contract.selectedTemplateId) {
                    try {
                        const templateData = await ContractsTemplatesService.getTemplateById(contract.selectedTemplateId);
                        this.selectedTemplate = templateData.data.data;
                    } catch (error) {
                        console.error('Erro ao buscar template:', error);
                        this.selectedTemplate = null;
                    }
                }
                
                // Download attached contract if available
                if (contract.currentAttachedContract && contract.currentAttachedContract.path) {
                    const fileBlob = await ContractRequestStorage.downloadImg(this.currentContract.currentAttachedContract.path);
                    this.fileToShow = new File([fileBlob], this.currentContract.currentAttachedContract.name, {
                        type: fileBlob.type
                    });
                } else {
                    this.fileToShow = null;
                }
            } catch (e) {
                console.error("error: ", e)
                this.isLoading = false;
            } finally {
                this.isLoading = false;
            }
        },
    },
    watch: {
        async open(newVal) {
            if (newVal) {
                this.isContentLoading = true;
                try {
                    if (this.idContract) {
                        await this.setContractForEditing(this.idContract);
                    } else {
                        this.resetContractState();
                    }
                } catch (error) {
                    console.error('Error loading contract:', error);
                    showErrorSnackBar('Erro ao carregar o contrato');
                } finally {
                    this.isContentLoading = false;
                }
            } else {
                this.resetContractState();
            }
        }
    },
    async created() {
    }
}
